import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Timeline } from "./Timeline";
import { MdWork, MdAccessTime, MdExpandMore, MdCode, MdLaunch, MdTrendingUp, MdPalette, MdPeople, MdLaptop, MdCheckCircle, MdLightbulbOutline, MdDescription, MdBusiness } from "react-icons/md";
import { SiReact, SiTypescript, SiDocker, SiFirebase, SiTailwindcss, SiAntdesign, SiCanva } from "react-icons/si";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Img1 from '../assets/SPIT.png'
import Img2 from '../assets/virtue.png'
import Img3 from '../assets/hekau.jpg'

// Accordion Component
const AccordionItem = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-zinc-200 dark:border-zinc-800 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 px-6 flex items-center justify-between text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors duration-150"
      >
        <span className="text-lg md:text-xl font-medium text-zinc-800 dark:text-zinc-200">
          {title}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <MdExpandMore className="text-zinc-500 dark:text-zinc-400 text-sm" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 pt-2">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Experience data
const experienceData = {
  SPIT: {
    company: "SP IT Solutions",
    role: "Frontend Engineer – Core Banking Platform",
    tenure: "Feb 2025 – Present",
    achievements: [
      "Built a Core Banking Application supporting financial operations for 500,000+ customers across 50+ branches in collaboration with backend and product teams.",
      "Architected a backend-driven, configuration-based frontend where forms, tables, modules, validations, and notifications render dynamically via schemas without redeployment.",
      "Developed 15+ dynamic multi-step form engines with draft recovery, reducing form abandonment by 70%.",
      "Engineered a reusable data table framework powering 50+ high-volume datasets with server-driven filtering, pagination, and column controls.",
      "Reduced UI load time from 4s to 1.1s and lowered API traffic by 85% using SWC optimization, code splitting, React Query caching, and persisted Zustand state.",
      "Designed RBAC architecture with route-, role-, component-, and data-level permission enforcement, JWT session handling, and XSS protection, maintaining zero unauthorized access incidents."
    ],
    learnings: [
      "Designing scalable backend-driven frontend systems that evolve without frequent deployments.",
      "Balancing performance optimization with enterprise-level scalability.",
      "Implementing secure, banking-grade access control aligned with strict compliance needs."
    ],
    techStack: [
      "Next.js",
      "React",
      "TypeScript",
      "React Query",
      "Zustand",
      "Formik",
      "Yup",
      "Tailwind CSS",
      "shadcn/ui",
      "Redis",
      "JWT"
    ]
  },

  VirtueTech: {
    company: "VirtueTech Services",
    role: "Lead Frontend React Developer",
    tenure: "Nov 2023 – Feb 2025",
    achievements: [
      "Worked on a  AI-powered HR automation platform that publishes jobs, auto-shortlists candidates, and runs AI-driven interview workflows.",
      "Built recruiter and admin dashboards to track applicant pipelines, interview analytics, and hiring performance metrics.",
      "Delivered 10+ client projects including HR tech platforms and corporate websites with scalable, responsive production interfaces.",
      "Improved performance, SEO, and UX across platforms, increasing organic traffic by 50% while managing multiple parallel projects."
    ],
    learnings: [
      "Owning frontend architecture in a fast-paced startup environment.",
      "Integrating AI-driven workflows into real-world production systems.",
      "Balancing rapid delivery with long-term maintainability."
    ],
    techStack: [
      "React",
      "TypeScript",
      "React Query",
      "Zustand",
      "Tailwind CSS",
      "Ant Design",
      "Firebase",
      "Docker",
      "SEO Optimization"
    ]
  },

  Anubits: {
    company: "Anubits Solution",
    role: "Frontend Developer Intern",
    tenure: "6 Months",
    achievements: [
      "Worked on client-facing web projects focusing on frontend development and API integration.",
      "Contributed to key pages of the Arc Bulletin news platform using Next.js, improving page load speed by 12%.",
      "Implemented responsive UI components and collaborated with backend and design teams for seamless feature delivery.",
      "Enhanced user engagement through performance-focused optimizations and structured component development."
    ],
    learnings: [
      "Working with real client requirements and production deadlines.",
      "Improving frontend performance through structured implementation.",
      "Collaborating effectively with cross-functional teams."
    ],
    techStack: [
      "React",
      "Next.js",
      "JavaScript",
      "HTML",
      "CSS",
      "Figma"
    ]
  }
};


const WorkExpGuide = ({ onDismiss, isVisible, buttonRef }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      const updateCoords = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const isMobileView = window.innerWidth < 768;
          setIsMobile(isMobileView);
          setCoords({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      };

      updateCoords();
      window.addEventListener('resize', updateCoords);

      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "0";
        window.removeEventListener('resize', updateCoords);
      };
    }
  }, [isVisible, buttonRef]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Professional Spotlight Mask / Click to close overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99998] pointer-events-auto bg-black/60 backdrop-blur-[2px] touch-none"
            onClick={onDismiss}
            style={!isMobile ? {
              clipPath: `polygon(
                0% 0%, 
                0% 100%, 
                ${coords.left - 10}px 100%, 
                ${coords.left - 10}px ${coords.top - 10}px, 
                ${coords.left + coords.width + 10}px ${coords.top - 10}px, 
                ${coords.left + coords.width + 10}px ${coords.top + coords.height + 10}px, 
                ${coords.left - 10}px ${coords.top + coords.height + 10}px, 
                ${coords.left - 10}px 100%, 
                100% 100%, 
                100% 0%
              )`,
            } : {}}
          />

          <div
            className={`fixed z-[99999] pointer-events-none flex flex-col items-center ${isMobile ? 'inset-0 justify-center px-4' : ''}`}
            style={!isMobile ? {
              top: coords.top + coords.height / 2,
              left: coords.left + coords.width + 20,
              transform: 'translateY(-50%)',
            } : {}}
          >
            {/* Guide Card */}
            <motion.div
              initial={{ opacity: 0, x: isMobile ? 0 : -10, y: isMobile ? 20 : 0, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: isMobile ? 0 : -10, y: isMobile ? 20 : 0, scale: 0.95 }}
              className="pointer-events-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl shadow-2xl w-[260px] sm:w-[300px] md:w-[340px] font-sans overflow-hidden"
            >
              <div className="relative space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-3xl sm:text-4xl">💡</span>
                  <h3 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">Explore My Work</h3>
                </div>

                <p className="text-zinc-600 dark:text-zinc-400 text-xs sm:text-sm leading-relaxed">
                  I've documented my key contributions and impact for each role. Click <span className="font-bold text-blue-600 dark:text-blue-400">Know My Work</span> to see the details.
                </p>

                <div className="pt-1 sm:pt-2">
                  <button
                    onClick={onDismiss}
                    className="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold py-2.5 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-md text-xs sm:text-sm"
                  >
                    Got it!
                  </button>
                </div>
              </div>

              {!isMobile && (
                <>
                  {/* Arrow pointing left (towards the button) */}
                  <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-zinc-900 border-b border-l border-zinc-200 dark:border-zinc-800 rotate-45" />
                </>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Experience Card with Accordions
const ExperienceCard = ({ company }) => {
  const data = experienceData[company];

  return (
    <div className="bg-white dark:bg-zinc-900/50 rounded-2xl shadow-md dark:shadow-zinc-900/50 overflow-hidden border border-zinc-200 dark:border-zinc-800 mb-8">
      {/* Key Contributions Section */}
      <AccordionItem title="Key Contributions" defaultOpen={true}>
        <ul className="space-y-2.5">
          {data.achievements.map((achievement, index) => (
            <li
              key={index}
              className="flex items-start gap-2.5"
            >
              <MdCheckCircle className="text-green-600 dark:text-green-400 mt-1 flex-shrink-0 text-sm" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                {achievement}
              </span>
            </li>
          ))}
        </ul>
      </AccordionItem>

      {/* Tech Stack Section */}
      <AccordionItem title="Tech Stack">
        <div className="flex flex-wrap gap-2">
          {data.techStack.map((tech, index) => (
            <span
              key={index}
              className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 rounded-full text-sm font-normal"
            >
              {tech}
            </span>
          ))}
        </div>
      </AccordionItem>

      {/* What I Learned Section */}
      {data.learnings && (
        <AccordionItem title="What I Learned">
          <ul className="space-y-2.5">
            {data.learnings.map((learning, index) => (
              <li
                key={index}
                className="flex items-start gap-2.5"
              >
                <MdLightbulbOutline className="text-yellow-600 dark:text-yellow-400 mt-1 flex-shrink-0 text-sm" />
                <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  {learning}
                </span>
              </li>
            ))}
          </ul>
        </AccordionItem>
      )}
    </div>
  );
};

export function Experience() {
  const [expandedCompany, setExpandedCompany] = useState(null);

  const [showGuide, setShowGuide] = useState(false);
  const buttonRef = useRef(null);
  const { ref: guideRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    const hasSeen = localStorage.getItem("has_seen_work_exp_guide_v2");
    if (!hasSeen && inView) {
      setTimeout(() => {
        setShowGuide(true);
      }, 800);
    }
  }, [inView]);

  const handleDismiss = () => {
    setShowGuide(false);
    localStorage.setItem("has_seen_work_exp_guide_v2", "true");
  };

  const data = [
    {
      title: "2025",
      content: (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <WorkExpGuide isVisible={showGuide} onDismiss={handleDismiss} buttonRef={buttonRef} />
          <div className='flex flex-col justify-start items-start'>
            <div className="flex flex-row justify-start gap-2 items-center">
              <div className='flex flex-col  lg:flex-row justify-start items-start lg:items-center gap-4'>
                <img src={Img1} alt="SP IT Solutions" className='md:w-20 md:h-20 w-16 h-16 object-contain' />
              </div>
              <div className="flex flex-col justify-start items-start">
                <span className="md:text-6xl sm:text-4xl text-2xl text-zinc-800/90 dark:text-zinc-200/90 font-bold md:tracking-normal">SP IT Solutions</span>

                <span className="lg:text-3xl sm:text-2xl text-lg dark:text-zinc-600 text-zinc-700  pt-0.5 lg:pt-1 font-semibold md:tracking-normal">
                  Executive Frontend Developer
                </span>
              </div></div>
            <span className='lg:py-4 py-6 flex flex-row justify-center gap-2 items-center'>
              <MdAccessTime className='dark:text-zinc-300 text-zinc-900' />
              <span className='dark:text-zinc-300 text-zinc-900 text-xl md:tracking-normal tracking-wide font-medium'>
                Feb 2025 - Present
              </span>
            </span>
          </div>

          <p className="text-neutral-800 md:tracking-normal dark:text-neutral-500 lg:tracking-normal tracking-wide text-base md:text-xl font-normal my-4">
            Currently working on a large-scale Core Banking platform used across 50+ branches, supporting operations for over 500,000 customers. I contribute to building and structuring the system in a way that keeps it scalable, secure, and efficient for real-world banking workflows.
          </p>

          <div ref={guideRef} className="inline-block relative">
            <button
              ref={buttonRef}
              onClick={() => setExpandedCompany(expandedCompany === "SPIT" ? null : "SPIT")}
              className='dark:bg-white bg-zinc-800 dark:text-zinc-900 text-zinc-200 lg:px-8 px-4 lg:text-xl text-base font-medium py-2 lg:py-2.5 mt-4 lg:mt-6 rounded-full hover:bg-zinc-300 dark:hover:bg-zinc-100 transition-all duration-200 inline-flex items-center gap-2'
            >
              {expandedCompany === "SPIT" ? "Hide Details" : "Know My Work"}
              <span
                className="inline-block transition-transform duration-200"
                style={{ transform: expandedCompany === "SPIT" ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <MdExpandMore className="text-sm" />
              </span>
            </button>
          </div>

          <AnimatePresence>
            {expandedCompany === "SPIT" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="overflow-hidden"
              >
                <div className="mt-6">
                  <ExperienceCard company="SPIT" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ),
    },
    {
      title: "2024",
      content: (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className='flex flex-col justify-start items-start'>
            <div className="flex flex-row justify-start gap-2 items-center">
              <div className='flex flex-col  lg:flex-row justify-start items-start lg:items-center gap-4'>
                <img src={Img2} alt="VirtueTech Services" className='md:w-20 md:h-20 w-16 h-16 object-contain' />
              </div>
              <div className="flex flex-col justify-start items-start">
                <span className="md:text-6xl sm:text-4xl text-2xl text-zinc-800/90 dark:text-zinc-200/90 font-bold md:tracking-normal">VirtueTech Services</span>

                <span className="lg:text-3xl sm:text-2xl text-lg dark:text-zinc-600 text-zinc-700  pt-0.5 lg:pt-1 font-semibold md:tracking-normal">
                  Frontend Developer
                </span>
              </div></div>
            <span className='lg:py-4 py-6 flex flex-row justify-center gap-2 items-center'>
              <MdAccessTime className='dark:text-zinc-300 text-zinc-900' />
              <span className='dark:text-zinc-300 text-zinc-900 text-xl md:tracking-normal tracking-wide font-medium'>Nov 2023 - Feb 2025</span>
            </span>
          </div>

          <p className="text-neutral-800 md:tracking-normal dark:text-neutral-500 lg:tracking-normal tracking-wide text-base md:text-xl font-normal my-4">


            Worked on a AI-powered HR automation platform, helping streamline hiring workflows and interview processes. Worked closely with product and design teams in a fast-paced startup environment, delivering multiple client and internal projects.
          </p>

          <button
            onClick={() => setExpandedCompany(expandedCompany === "VirtueTech" ? null : "VirtueTech")}
            className='dark:bg-white bg-zinc-800 dark:text-zinc-900 text-zinc-200 lg:px-8 px-4 lg:text-xl text-base font-medium py-2 lg:py-2.5 mt-4 lg:mt-6 rounded-full hover:bg-zinc-300 dark:hover:bg-zinc-100 transition-all duration-200 inline-flex items-center gap-2'
          >
            {expandedCompany === "VirtueTech" ? "Hide Details" : "Know My Work"}
            <span
              className="inline-block transition-transform duration-200"
              style={{ transform: expandedCompany === "VirtueTech" ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <MdExpandMore className="text-sm" />
            </span>
          </button>

          <AnimatePresence>
            {expandedCompany === "VirtueTech" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="overflow-hidden"
              >
                <div className="mt-6">
                  <ExperienceCard company="VirtueTech" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ),
    },
    {
      title: "2023",
      content: (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className='flex flex-col justify-start items-start'>
            <div className='flex flex-col lg:flex-row justify-start items-start lg:items-center gap-4'>
              <img src={Img3} alt="Anubits Solution" className='md:w-16 rounded-lg md:h-16 w-12 h-12 scale-105 object-contain' />
              <span className="md:text-6xl sm:text-4xl text-2xl text-zinc-800/90 dark:text-zinc-200/90 font-bold md:tracking-normal">Anubits Solution</span>
            </div>
            <span className="lg:text-3xl sm:text-2xl text-lg dark:text-zinc-600 text-zinc-700 lg:pl-20 pt-0.5 lg:pt-2 font-semibold md:tracking-normal">Frontend Developer (Internship) </span>
            <span className='py-4 flex flex-row justify-center gap-2 items-center'>
              <MdAccessTime className='dark:text-zinc-300 text-zinc-900' />
              <span className='dark:text-zinc-300 text-zinc-900 text-xl md:tracking-normal tracking-wide font-medium'>6 months</span>
            </span>
          </div>

          <p className="text-neutral-800 md:tracking-normal dark:text-neutral-500 lg:tracking-normal tracking-wide text-base md:text-xl font-normal my-4">
            Started my journey as a frontend intern, working on client projects and contributing to a news platform. Gained hands-on experience in real-world development, collaboration, and performance-focused implementation.  Played a key role in building two main pages for the Arc Bulletin news website, delivering clean and functional designs. Reference: <a href="https://www.thearcweb.com/home" className="text-zinc-950 font-semibold dark:text-zinc-50" target="_blank">The ARC Web</a>
          </p>

          <button
            onClick={() => setExpandedCompany(expandedCompany === "Anubits" ? null : "Anubits")}
            className='dark:bg-white bg-zinc-800 dark:text-zinc-900 text-zinc-200 lg:px-8 px-4 lg:text-xl text-base font-medium py-2 lg:py-2.5 mt-4 lg:mt-6 rounded-full hover:bg-zinc-300 dark:hover:bg-zinc-100 transition-all duration-200 inline-flex items-center gap-2'
          >
            {expandedCompany === "Anubits" ? "Hide Details" : "Know My Work"}
            <span
              className="inline-block transition-transform duration-200"
              style={{ transform: expandedCompany === "Anubits" ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <MdExpandMore className="text-sm" />
            </span>
          </button>

          <AnimatePresence>
            {expandedCompany === "Anubits" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="overflow-hidden"
              >
                <div className="mt-6">
                  <ExperienceCard company="Anubits" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ),
    },
  ];

  return (
    <div id='works'>
      <Timeline data={data} />
    </div>
  );
}

